/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx } from '@ba-ui-toolkit/utils';
import Button from '@ba-ui-toolkit/button';
import ToolTip from '@ba-ui-toolkit/tooltip';
import TruncatedText from '@ba-ui-toolkit/truncatedtext';
import styles from './Tags.scss';
import stringResource from './nls/StringResource';
import colorPalette from './colors';

const tagsClassName = styles.tagsClassName.replace(/"/g, '');

function getString(value) {
	return stringResource.get(value);
}

export default class Tags extends Component {
	static propTypes = {
		children: PropTypes.node,
		className: PropTypes.string,
		disabled: PropTypes.bool,
		fullWidth: PropTypes.bool,
		getLocaleString: PropTypes.func,
		tagActionIcon: PropTypes.object,
		tags: PropTypes.array.isRequired,
		removable: PropTypes.bool,
		editLabel: PropTypes.func,
		maxTagWidth: PropTypes.number,
	};

	static defaultProps = {
		disabled: false,
		getLocaleString: getString,
		removable: true,
	};

	_optionId = index => `tag_${index}`;
	_nodeRef = node => {
		this._node = node;
	};
	_listRef = node => {
		this._list = node;
	};

	_labelStyles = () =>
		this.props.maxTagWidth
			? { 'max-width': `${this.props.maxTagWidth}px`, display: 'flex' }
			: {};

	_labelTruncatedText = ({ label }) => <TruncatedText value={label} />;

	_labelLabel = ({ label }) => label;

	_renderLabel = tag =>
		this.props.maxTagWidth ? this._labelTruncatedText(tag) : this._labelLabel(tag);

	_renderTooltipButton = ({ value, label, color }) => {
		const { removable, disabled, getLocaleString, tagActionIcon } = this.props;
		return !removable ? null : (
			<Button
				aria-label={`${getLocaleString('remove')} ${label}`}
				disabled={disabled}
				icon={tagActionIcon.id}
				onClick={tagActionIcon.onClick}
				size={'small'}
				tabIndex={0}
				tagvalue={value}
				variant={'inline'}
				style={{
					color:
						color in colorPalette
							? colorPalette[color].font
							: colorPalette.default.font,
				}}
			/>
		);
	};

	_renderTooltip = tag => {
		const { tagActionIcon, disabled, getLocaleString } = this.props;
		return !tagActionIcon ? null : (
			<ToolTip disabled={disabled} orient="top" title={getLocaleString('remove')}>
				{this._renderTooltipButton(tag)}
			</ToolTip>
		);
	};

	render() {
		const { className, fullWidth, tags, children, removable, editLabel } = this.props;

		return (
			<div className={cx(className, tagsClassName, { fullWidth })} ref={this._nodeRef}>
				<div className={`${tagsClassName}__list`} ref={this._listRef}>
					{tags.map(tag => {
						const { value, color } = tag;
						return (
							<span
								className={
									removable
										? `${tagsClassName}__listItem ${tagsClassName}__listItem_clickable`
										: `${tagsClassName}__listItem`
								}
								key={this._optionId(value)}
								onDoubleClick={e => editLabel(tag, e)}
								style={{
									color:
										color in colorPalette
											? colorPalette[color].font
											: colorPalette.default.font,
									background:
										color in colorPalette
											? colorPalette[color].background
											: colorPalette.default.background,
									...this._labelStyles(),
								}}
							>
								{this._renderLabel(tag)}
								{this._renderTooltip(tag)}
							</span>
						);
					})}
					{children}
				</div>
			</div>
		);
	}
}
